import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Tier constants
export const TIER_FREE = 'FREE';
export const TIER_PREMIUM_INDIVIDUAL = 'PREMIUM_INDIVIDUAL';
export const TIER_PREMIUM_HOUSEHOLD = 'PREMIUM_HOUSEHOLD';

// Role constants
export const ROLE_ADMIN = 'ADMIN';
export const ROLE_MEMBER = 'MEMBER';
export const ROLE_NONE = 'NONE';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState({
    loading: true,
    status: 'none',
    subscriptionTier: TIER_FREE,
    householdRole: ROLE_NONE,
    canUsePlaid: false,
    canUseFullHistory: false,
    canUseAdvancedAI: false,
    lifetimeFree: false,
    tier: null,
    expiresAt: null
  });

  const fetchSubscription = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setSubscription(prev => ({ ...prev, loading: false }));
        return;
      }

      const res = await fetch(`${API}/api/subscription/status`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubscription({
          loading: false,
          status: data.status || 'none',
          subscriptionTier: data.subscription_tier || TIER_FREE,
          householdRole: data.household_role || ROLE_NONE,
          canUsePlaid: data.can_use_plaid || false,
          canUseFullHistory: data.can_use_full_history || false,
          canUseAdvancedAI: data.can_use_advanced_ai || false,
          lifetimeFree: data.lifetime_free || false,
          tier: data.tier,
          expiresAt: data.expires_at,
          householdPremiumInherited: data.household_premium_inherited || false
        });
      } else {
        setSubscription(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Helper functions
  // Premium access tiers - all valid subscription statuses that grant premium features
  const PREMIUM_STATUSES = ['premium', 'family', 'household', 'lifetime_free', 'active'];
  
  const isPremium = () => {
    // Check subscription tier constants
    if (subscription.subscriptionTier === TIER_PREMIUM_INDIVIDUAL) return true;
    if (subscription.subscriptionTier === TIER_PREMIUM_HOUSEHOLD) return true;
    
    // Check if status is a premium status
    if (PREMIUM_STATUSES.includes(subscription.status)) return true;
    
    // Check explicit premium flags
    if (subscription.lifetimeFree) return true;
    if (subscription.hasAccess) return true;
    
    return false;
  };

  const isHouseholdAdmin = () => {
    return subscription.householdRole === ROLE_ADMIN;
  };

  const isHouseholdMember = () => {
    return subscription.householdRole === ROLE_MEMBER;
  };

  const canAccessFeature = (feature) => {
    switch (feature) {
      case 'plaid':
      case 'bank_sync':
        return subscription.canUsePlaid;
      case 'full_history':
      case 'history':
        return subscription.canUseFullHistory;
      case 'advanced_ai':
      case 'ai':
        return subscription.canUseAdvancedAI;
      default:
        return isPremium();
    }
  };

  const value = {
    ...subscription,
    refreshSubscription: fetchSubscription,
    isPremium,
    isHouseholdAdmin,
    isHouseholdMember,
    canAccessFeature
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
